import React from 'react';
import { useSelector } from 'react-redux';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { useCurrentStateAndParams } from '@wix/tpa-router/react';

import { GroupAppKey } from 'store/groups/types';
import {
  selectCanApproveMembers,
  selectGroupIdBySlug,
  selectIsAppAvailable,
  selectIsJoinedGroupMember,
} from 'store/selectors';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';

import { Card } from 'wui/Card';
import { Divider } from 'wui/Divider';

import { Column, Layout } from '../Layout';
import { RestrictedState } from '../RestrictedState';

import { GroupMembers } from './GroupMembers';
import { JoinRequests } from './JoinRequests';
import { MembersPageFilters } from './MembersPageFilters';

export function MembersPage() {
  const { params } = useCurrentStateAndParams();
  const { isMobile } = useEnvironment();
  const { members$ } = useController();

  const groupId = useSelector(selectGroupIdBySlug(params.slug));
  const isJoined = useSelector(selectIsJoinedGroupMember(groupId));
  const canApprove = useSelector(selectCanApproveMembers(groupId));
  const isAvailable = useSelector(
    selectIsAppAvailable({
      groupId,
      application: GroupAppKey.MEMBERS_APP,
    }),
  );

  useDidUpdate(() => {
    if (isAvailable) {
      members$.query(groupId, params);
    }
  }, [isAvailable, isJoined]);

  useDidUpdate(() => {
    if (canApprove) {
      members$.fetchJoinRequests(groupId);
    }
  }, [canApprove]);

  return (
    <RestrictedState application={GroupAppKey.MEMBERS_APP}>
      <Layout>
        <Column>
          <JoinRequests
            groupId={groupId}
            initiallyExpanded={params.expandJoinedRequests}
          />
          <Card sideBorders={!isMobile}>
            <MembersPageFilters />
            <Divider inset />
            <GroupMembers groupId={groupId} />
          </Card>
        </Column>
      </Layout>
    </RestrictedState>
  );
}

MembersPage.displayName = 'MembersPage';
